import FolderIcon from "@/assets/images/icons/folder.svg";
import { AppDialog } from "@/components/ui/AppDialog";
import {
  BodyText,
  CaptionText,
  CardTitle,
} from "@/components/ui/Typography";
import { BRAND_COLORS } from "@/lib/brandColors";
import { UserFolder } from "@/supabase/services/userFolders";
import React, { memo, useEffect, useState } from "react";
import { Pressable, StyleSheet, TextInput } from "react-native";
import { XStack, YStack } from "tamagui";

type FolderNameDialogProps = {
  open: boolean;
  title: string;
  confirmLabel: string;
  busyLabel: string;
  busy: boolean;
  initialValue?: string;
  onClose: () => void;
  onSubmit: (name: string) => void;
};

const FolderNameDialog = memo(function FolderNameDialog({
  open,
  title,
  confirmLabel,
  busyLabel,
  busy,
  initialValue = "",
  onClose,
  onSubmit,
}: FolderNameDialogProps) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  return (
    <AppDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={title}
    >
      <TextInput
        value={value}
        onChangeText={setValue}
        onSubmitEditing={() => onSubmit(value)}
        placeholder="Folder name"
        placeholderTextColor={BRAND_COLORS.muted}
        autoFocus
        selectTextOnFocus
        style={styles.input}
      />
      <Pressable
        onPress={() => onSubmit(value)}
        disabled={!value.trim() || busy}
        style={[
          styles.primaryBtn,
          (!value.trim() || busy) && styles.btnDisabled,
        ]}
      >
        <CaptionText color={BRAND_COLORS.cardBg} fontWeight="600">
          {busy ? busyLabel : confirmLabel}
        </CaptionText>
      </Pressable>
    </AppDialog>
  );
});

type RenameFolderDialogProps = {
  folder: UserFolder | null;
  busy: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
};

/** Owns rename input state so typing doesn’t re-render the bookmarks list */
export const RenameFolderDialog = memo(function RenameFolderDialog({
  folder,
  busy,
  onClose,
  onSave,
}: RenameFolderDialogProps) {
  return (
    <FolderNameDialog
      open={!!folder}
      title="Rename folder"
      confirmLabel="Save"
      busyLabel="Saving…"
      busy={busy}
      initialValue={folder?.title ?? ""}
      onClose={onClose}
      onSubmit={onSave}
    />
  );
});

type CreateFolderDialogProps = {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onCreate: (name: string) => void;
};

export const CreateFolderDialog = memo(function CreateFolderDialog({
  open,
  busy,
  onClose,
  onCreate,
}: CreateFolderDialogProps) {
  return (
    <FolderNameDialog
      open={open}
      title="New folder"
      confirmLabel="Create"
      busyLabel="Creating…"
      busy={busy}
      initialValue=""
      onClose={onClose}
      onSubmit={onCreate}
    />
  );
});

type DeleteFolderDialogProps = {
  folder: UserFolder | null;
  bookmarkCount: number;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const DeleteFolderDialog = memo(function DeleteFolderDialog({
  folder,
  bookmarkCount,
  busy,
  onClose,
  onConfirm,
}: DeleteFolderDialogProps) {
  return (
    <AppDialog
      open={!!folder}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Delete folder"
    >
      <BodyText color={BRAND_COLORS.muted}>
        {folder && bookmarkCount > 0
          ? `“${folder.title}” and ${bookmarkCount} bookmark${bookmarkCount === 1 ? "" : "s"} will be removed.`
          : folder
            ? `“${folder.title}” will be removed.`
            : ""}
      </BodyText>
      <XStack gap={12} width="100%">
        <Pressable
          onPress={onClose}
          disabled={busy}
          style={[styles.secondaryBtn, busy && styles.btnDisabled]}
        >
          <CaptionText color={BRAND_COLORS.text} fontWeight="600">
            Cancel
          </CaptionText>
        </Pressable>
        <Pressable
          onPress={onConfirm}
          disabled={busy}
          style={[styles.dangerBtn, busy && styles.btnDisabled]}
        >
          <CaptionText color={BRAND_COLORS.cardBg} fontWeight="600">
            {busy ? "Deleting…" : "Delete"}
          </CaptionText>
        </Pressable>
      </XStack>
    </AppDialog>
  );
});

type MoveFolderOption = {
  id: number;
  title: string;
};

type MoveBookmarkDialogProps = {
  open: boolean;
  title: string | null;
  targets: MoveFolderOption[];
  busy: boolean;
  onClose: () => void;
  onMove: (folderId: number) => void;
};

export const MoveBookmarkDialog = memo(function MoveBookmarkDialog({
  open,
  title,
  targets,
  busy,
  onClose,
  onMove,
}: MoveBookmarkDialogProps) {
  return (
    <AppDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Move to folder"
    >
      {title ? (
        <BodyText color={BRAND_COLORS.muted} numberOfLines={2}>
          {title}
        </BodyText>
      ) : null}
      {targets.length === 0 ? (
        <BodyText color={BRAND_COLORS.muted}>
          No other folders yet. Create one first.
        </BodyText>
      ) : (
        <YStack
          borderWidth={1}
          borderColor={BRAND_COLORS.border}
          borderRadius={12}
          overflow="hidden"
          maxHeight={360}
        >
          {targets.map((folder, index) => (
            <Pressable
              key={folder.id}
              onPress={() => onMove(folder.id)}
              disabled={busy}
            >
              <XStack
                ai="center"
                gap="$3"
                px="$3.5"
                py="$3.5"
                borderTopWidth={index === 0 ? 0 : 1}
                borderTopColor="rgba(0,0,0,0.05)"
                opacity={busy ? 0.5 : 1}
              >
                <YStack
                  w={40}
                  h={40}
                  br={12}
                  ai="center"
                  jc="center"
                  bg={BRAND_COLORS.pageBg}
                >
                  <FolderIcon
                    width={16}
                    height={16}
                    color={BRAND_COLORS.muted}
                  />
                </YStack>
                <CardTitle flex={1} fontWeight="600" color={BRAND_COLORS.text}>
                  {folder.title}
                </CardTitle>
                <CaptionText color={BRAND_COLORS.linkAccent} fontWeight="600">
                  Move here
                </CaptionText>
              </XStack>
            </Pressable>
          ))}
        </YStack>
      )}
    </AppDialog>
  );
});

const styles = StyleSheet.create({
  input: {
    width: "100%",
    borderWidth: 1,
    borderColor: BRAND_COLORS.border,
    borderRadius: 10,
    backgroundColor: BRAND_COLORS.pageBg,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: BRAND_COLORS.text,
  },
  primaryBtn: {
    backgroundColor: BRAND_COLORS.headerBg,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 999,
    alignItems: "center",
  },
  secondaryBtn: {
    flex: 1,
    backgroundColor: BRAND_COLORS.pageBg,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 999,
    alignItems: "center",
  },
  dangerBtn: {
    flex: 1,
    backgroundColor: BRAND_COLORS.error,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 999,
    alignItems: "center",
  },
  btnDisabled: {
    opacity: 0.5,
  },
});
